import { withErrorBoundary } from 'app/components/error-boundary';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { ProgressIndicator } from 'app/design/primitives/progress-indicator';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';

import { useScopedTranslation } from 'app/hooks/i18n';
import type { NavigationScreen } from 'app/types/navigation';
import { useOnboarding } from 'app/hooks/use-onboarding';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View, type ViewStyle } from 'react-native';
import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai/index';
import { userInfoAtom } from 'app/atoms';
import { useOnboardingNavigation } from 'app/screens/onboarding/credentials';
import { usePageTitle } from 'app/utils/web-helpers';
import { getI18nLanguage } from 'app/lib/language';
import { GuidelinesAgreementBody } from 'app/screens/onboarding/guidelines-agreement-body';
import { useGuidelinesFlow } from 'app/screens/onboarding/hooks/use-guidelines-flow';
import { useGuidelinesForm } from 'app/screens/onboarding/hooks/use-guidelines-form';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

const GuidelinesScreenComponent: NavigationScreen<'guidelines'> = () => {
  const { t } = useScopedTranslation();
  const i18nLanguage = getI18nLanguage();
  const { control, formState, handleSubmit } = useGuidelinesForm();
  const insets = useSafeAreaInsets();
  const { current, max } = useOnboarding();
  const flow = useGuidelinesFlow({ handleSubmit });

  const setPageTitle = usePageTitle('guidelines');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const userInfo = useAtomValue(userInfoAtom);
  const { progressActiveColor } = useOnboardingNavigation(userInfo?.type);

  const paddingProps: ViewStyle = {
    paddingBottom: insets.bottom || theme.spacing['spacing.general.x8'],
    paddingLeft: insets.left || theme.spacing['spacing.general.x4'],
    paddingRight: insets.right || theme.spacing['spacing.general.x4'],
  };

  // Fix the problem with the missing accessory view in this screen
  // Ref: https://lepartageclub.atlassian.net/browse/LPC-1635
  const [blip, setBlip] = useState(false);
  useEffect(() => {
    const blipTimer = setTimeout(() => setBlip(true), 300);
    return () => clearTimeout(blipTimer);
  }, []);

  return (
    <Page
      accessory={
        blip ? (
          <Accessory>
            <Button
              disabled={!formState.isValid}
              loading={flow.isLoading}
              marginHorizontal="spacing.general.x4"
              marginTop="spacing.general.x4"
              onPress={() => void flow.onSubmit()}
              testID="guidelines-submit-button"
              variant="primary"
            >
              {t('primaryButtonText')}
            </Button>
          </Accessory>
        ) : null
      }
    >
      <View style={{ ...paddingProps, flex: 1 }}>
        <ProgressIndicator activeColor={progressActiveColor} index={current} length={max} />

        <Text
          fontWeight="600"
          marginBottom="spacing.general.x4"
          marginTop="spacing.general.x8"
          textAlign="center"
          variant="headerXSmall"
        >
          {t('title')}
        </Text>

        <GuidelinesAgreementBody
          bullets={flow.bullets}
          control={control}
          i18nLanguage={i18nLanguage}
          t={t}
          user={flow.user}
        />
      </View>
    </Page>
  );
};

export const GuidelinesScreen: NavigationScreen<'guidelines'> = withErrorBoundary(GuidelinesScreenComponent);
